import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { resourcesApi, reservationsApi } from '../services/api';
import { Resource, CalendarEvent, Reservation } from '../types';

const Dashboard: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [reservations, setReservations] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get current date in ISO format
        const today = new Date();
        const startDate = today.toISOString();
        
        // Get date 30 days from now
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 30);
        
        // Fetch resources and upcoming reservations
        const [resourcesData, reservationsData] = await Promise.all([
          resourcesApi.getAll(),
          reservationsApi.getAll(startDate, endDate.toISOString())
        ]);
        
        setResources(resourcesData);
        
        // Map reservation data to calendar event format
        const calendarEvents: CalendarEvent[] = reservationsData.map(reservation => ({
          id: reservation.id,
          title: reservation.notes || 'Reservation',
          start: reservation.startDate,
          end: reservation.endDate,
          resourceId: reservation.resourceId,
          userName: 'User' // We don't have the user name in the basic reservation data
        }));
        
        setReservations(calendarEvents);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate dashboard stats
  const activeResources = resources.filter(r => r.isActive).length;
  const upcomingReservations = reservations.length;
  
  // Get today's reservations
  const today = new Date().toISOString().split('T')[0];
  const todayReservations = reservations.filter(r => 
    (r.start && r.start.startsWith(today)) || (r.end && r.end.startsWith(today))
  ).length;

  if (loading) {
    return <Typography>Loading dashboard...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
        {/* Total Resources Card */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Resources
            </Typography>
            <Typography variant="h5" component="div">
              {resources.length}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Active Resources Card */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Active Resources
            </Typography>
            <Typography variant="h5" component="div">
              {activeResources}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Today's Reservations Card */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Today's Reservations
            </Typography>
            <Typography variant="h5" component="div">
              {todayReservations}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Upcoming Reservations Card */}
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Upcoming Reservations (30 days)
            </Typography>
            <Typography variant="h5" component="div">
              {upcomingReservations}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
